import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaPlus, FaTimes, FaCloudUploadAlt, FaEdit } from 'react-icons/fa';
import { useMobile } from '@/hooks/useMobile';

type TabType = 'personal' | 'work' | 'documents' | 'acknowledgement';

const CandidateInformation: React.FC = () => {
  const { isMobile } = useMobile();
  const [activeTab, setActiveTab] = useState<TabType>('personal');
  const [mobileSectionOpen, setMobileSectionOpen] = useState<TabType | null>(null);
  
  // Form state for Personal Information
  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    mobileNumber: '',
    emailAddress: '',
    ssn: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    emergencyContactFirstName: '',
    emergencyContactLastName: '',
    emergencyContactNumber: '',
    relationship: '',
  });

  // Form state for Work Experience
  interface WorkExperience {
    id: string;
    previousEmployer: string;
    previousJobTitle: string;
    durationYears: string;
    durationMonths: string;
    totalYearsOfExperience: string;
    whatDidYouLike: string;
    reasonForLeaving: string;
  }

  const [workExperiences, setWorkExperiences] = useState<WorkExperience[]>([
    {
      id: Date.now().toString(),
      previousEmployer: '',
      previousJobTitle: '',
      durationYears: '',
      durationMonths: '',
      totalYearsOfExperience: '',
      whatDidYouLike: '',
      reasonForLeaving: '',
    },
  ]);

  // Form state for Documents
  const [documents, setDocuments] = useState({
    governmentIdType: '',
    governmentIdFile: null as File | null,
    ssnCardFile: null as File | null,
    workAuthorizationFile: null as File | null,
    proofOfAddressFile: null as File | null,
  });

  // Form state for Acknowledgement
  const [acknowledgement, setAcknowledgement] = useState({
    agreed: false,
  });

  const handleGovernmentIdTypeChange = (value: string) => {
    setDocuments(prev => ({ 
      ...prev, 
      governmentIdType: value,
      governmentIdFile: value ? prev.governmentIdFile : null // Clear file if type is cleared
    }));
  };

  const governmentIdTypes = [
    'Select ID Type',
    'Driver License',
    'US Passport',
  ];

  const tabs = [
    { id: 'personal' as TabType, label: 'Personal Information' },
    { id: 'work' as TabType, label: 'Work Experience' },
    { id: 'documents' as TabType, label: 'Documents' },
    { id: 'acknowledgement' as TabType, label: 'Acknowledgement & Consent' },
  ];

  const relationshipOptions = [
    'Select Relation',
    'Mother',
    'Father',
    'Spouse',
    'Sibling',
    'Child',
    'Other',
  ];

  const formatSSN = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 0) return '';
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const formatPhoneNumber = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 0) return '';
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === 'ssn') {
      const formatted = formatSSN(value);
      setPersonalInfo(prev => ({ ...prev, [field]: formatted }));
    } else if (field === 'mobileNumber' || field === 'emergencyContactNumber') {
      const formatted = formatPhoneNumber(value);
      setPersonalInfo(prev => ({ ...prev, [field]: formatted }));
    } else {
      setPersonalInfo(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSave = () => {
    // TODO: Implement save functionality
    console.log('Saving data...', { personalInfo, workExperiences });
    // You can add toast notification here
  };

  const handleNext = () => {
    const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1].id);
    }
  };

  const renderSaveNextButtons = () => (
    <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
      <button
        type="button"
        onClick={handleSave}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        Save
      </button>
      <button
        type="button"
        onClick={handleNext}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
          activeTab === tabs[tabs.length - 1].id
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
        disabled={activeTab === tabs[tabs.length - 1].id}
      >
        Next
      </button>
    </div>
  );

  const renderPersonalInformation = () => (
    <div className="space-y-6">
      <div className="space-y-4">

        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              First Name
            </label>
            <input
              type="text"
              className="input w-full py-2 text-sm"
              value={personalInfo.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              placeholder="Enter first name"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Last Name
            </label>
            <input
              type="text"
              className="input w-full py-2 text-sm"
              value={personalInfo.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              placeholder="Enter last name"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Date of Birth
            </label>
            <input
              type="date"
              className="input w-full py-2 text-sm"
              value={personalInfo.dateOfBirth}
              onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Gender
            </label>
            <select
              className="input w-full py-2 text-sm"
              value={personalInfo.gender}
              onChange={(e) => handleInputChange('gender', e.target.value)}
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Mobile Number
            </label>
            <input
              type="tel"
              className="input w-full py-2 text-sm"
              value={personalInfo.mobileNumber}
              onChange={(e) => handleInputChange('mobileNumber', e.target.value)}
              placeholder="XXX-XXX-XXXX"
              maxLength={12}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Email Address
            </label>
            <input
              type="email"
              className="input w-full py-2 text-sm"
              value={personalInfo.emailAddress}
              onChange={(e) => handleInputChange('emailAddress', e.target.value)}
              placeholder="Enter email address"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              SSN
            </label>
            <input
              type="text"
              className="input w-full py-2 text-sm"
              value={personalInfo.ssn}
              onChange={(e) => handleInputChange('ssn', e.target.value)}
              placeholder="XXX-XXX-XXXX"
              maxLength={12}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Address</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Address Line 1
            </label>
            <input
              type="text"
              className="input w-full py-2 text-sm"
              value={personalInfo.addressLine1}
              onChange={(e) => handleInputChange('addressLine1', e.target.value)}
              placeholder="Enter address line 1"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Address Line 2
            </label>
            <input
              type="text"
              className="input w-full py-2 text-sm"
              value={personalInfo.addressLine2}
              onChange={(e) => handleInputChange('addressLine2', e.target.value)}
              placeholder="Enter address line 2"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              City
            </label>
            <input
              type="text"
              className="input w-full py-2 text-sm"
              value={personalInfo.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              placeholder="Enter city"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              State
            </label>
            <input
              type="text"
              className="input w-full py-2 text-sm"
              value={personalInfo.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              placeholder="Enter state"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Zip
            </label>
            <input
              type="text"
              className="input w-full py-2 text-sm"
              value={personalInfo.zip}
              onChange={(e) => handleInputChange('zip', e.target.value)}
              placeholder="Enter zip code"
              maxLength={10}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Contact Details</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Emergency Contact First Name
            </label>
            <input
              type="text"
              className="input w-full py-2 text-sm"
              value={personalInfo.emergencyContactFirstName}
              onChange={(e) => handleInputChange('emergencyContactFirstName', e.target.value)}
              placeholder="Enter first name"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Emergency Contact Last Name
            </label>
            <input
              type="text"
              className="input w-full py-2 text-sm"
              value={personalInfo.emergencyContactLastName}
              onChange={(e) => handleInputChange('emergencyContactLastName', e.target.value)}
              placeholder="Enter last name"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Emergency Contact Number
            </label>
            <input
              type="tel"
              className="input w-full py-2 text-sm"
              value={personalInfo.emergencyContactNumber}
              onChange={(e) => handleInputChange('emergencyContactNumber', e.target.value)}
              placeholder="XXX-XXX-XXXX"
              maxLength={12}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Relationship with Employee
            </label>
            <select
              className="input w-full py-2 text-sm"
              value={personalInfo.relationship}
              onChange={(e) => handleInputChange('relationship', e.target.value)}
            >
              {relationshipOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {renderSaveNextButtons()}
    </div>
  );

  const handleWorkExperienceChange = (id: string, field: keyof WorkExperience, value: string) => {
    setWorkExperiences(prev =>
      prev.map(exp => (exp.id === id ? { ...exp, [field]: value } : exp))
    );
  };

  const removeWorkExperience = (id: string) => {
    if (workExperiences.length > 1) {
      setWorkExperiences(prev => prev.filter(exp => exp.id !== id));
    }
  };

  const yearsOptions = Array.from({ length: 51 }, (_, i) => i.toString());
  const monthsOptions = Array.from({ length: 12 }, (_, i) => i.toString());

  const renderWorkExperience = () => (
    <div className="space-y-6">
      <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-6">
        Recent Experience
      </h2>

      {workExperiences.map((experience, index) => (
        <div key={`experience-${experience.id}`} className="relative">
          {index > 0 && <div className="border-t border-gray-200 pt-6 mb-4"></div>}
          
          {/* Remove button - only show if more than one experience */}
          {workExperiences.length > 1 && (
            <button
              key={`remove-btn-${experience.id}`}
              type="button"
              onClick={() => removeWorkExperience(experience.id)}
              className="absolute top-0 right-0 p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors z-10"
              title="Remove this experience"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pr-8">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Previous Employer
                </label>
                <input
                  type="text"
                  className="input w-full py-2 text-sm"
                  value={experience.previousEmployer}
                  onChange={(e) => handleWorkExperienceChange(experience.id, 'previousEmployer', e.target.value)}
                  placeholder="Enter previous employer"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Duration of Employment
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    className="input w-full py-2 text-sm"
                    value={experience.durationYears}
                    onChange={(e) => handleWorkExperienceChange(experience.id, 'durationYears', e.target.value)}
                  >
                    <option value="">Years</option>
                    {yearsOptions.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  <select
                    className="input w-full py-2 text-sm"
                    value={experience.durationMonths}
                    onChange={(e) => handleWorkExperienceChange(experience.id, 'durationMonths', e.target.value)}
                  >
                    <option value="">Months</option>
                    {monthsOptions.map(month => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  What did you like about this employer
                </label>
                <textarea
                  className="input w-full py-2 text-sm"
                  rows={4}
                  value={experience.whatDidYouLike}
                  onChange={(e) => handleWorkExperienceChange(experience.id, 'whatDidYouLike', e.target.value)}
                  placeholder="Enter what you liked about this employer"
                />
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Previous Job Title
                </label>
                <input
                  type="text"
                  className="input w-full py-2 text-sm"
                  value={experience.previousJobTitle}
                  onChange={(e) => handleWorkExperienceChange(experience.id, 'previousJobTitle', e.target.value)}
                  placeholder="Enter previous job title"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Total Years of Experience
                </label>
                <input
                  type="text"
                  className="input w-full py-2 text-sm"
                  value={experience.totalYearsOfExperience}
                  onChange={(e) => handleWorkExperienceChange(experience.id, 'totalYearsOfExperience', e.target.value)}
                  placeholder="Enter total years of experience"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Reason for Leaving
                </label>
                <textarea
                  className="input w-full py-2 text-sm"
                  rows={4}
                  value={experience.reasonForLeaving}
                  onChange={(e) => handleWorkExperienceChange(experience.id, 'reasonForLeaving', e.target.value)}
                  placeholder="Enter reason for leaving"
                />
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Add Experience Button */}
      <div className="pt-4">
        <button
          type="button"
          disabled
          className="flex items-center space-x-2 text-gray-400 font-medium text-sm cursor-not-allowed opacity-50"
        >
          <div className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center">
            <FaPlus className="w-3 h-3 text-white" />
          </div>
          <span>Add Experience</span>
        </button>
      </div>

      {renderSaveNextButtons()}
    </div>
  );

  const handleDocumentChange = (field: string, value: string | File | null) => {
    setDocuments(prev => ({ ...prev, [field]: value }));
  };

  const handleFileInput = (field: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleDocumentChange(field, file);
  };

  const renderDocuments = () => (
    <div className="space-y-6">
      {/* Government ID */}
      <div className="space-y-2">
        <label className="block text-xs font-semibold text-gray-900">
          Government ID
        </label>
        <select
          className="input w-full py-2 text-sm"
          value={documents.governmentIdType}
          onChange={(e) => handleGovernmentIdTypeChange(e.target.value)}
        >
          {governmentIdTypes.map((type) => (
            <option key={type} value={type === 'Select ID Type' ? '' : type}>
              {type}
            </option>
          ))}
        </select>
        
        {/* Show file upload only after ID type is selected */}
        {documents.governmentIdType && (
          <div className="mt-2">
            <div className="relative">
              <input
                type="file"
                id="governmentId"
                className="hidden"
                onChange={(e) => handleFileInput('governmentIdFile', e)}
                accept=".pdf,.jpg,.jpeg,.png"
              />
              <label
                htmlFor="governmentId"
                className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                <div className="flex flex-col items-center space-y-2">
                  <FaCloudUploadAlt className="w-6 h-6 text-gray-400" />
                  <span className="text-xs text-gray-600">Select File</span>
                </div>
              </label>
            </div>
            {documents.governmentIdFile && (
              <p className="text-xs text-gray-600 mt-1">{documents.governmentIdFile.name}</p>
            )}
          </div>
        )}
      </div>

      {/* SSN Card */}
      <div className="space-y-2">
        <label className="block text-xs font-semibold text-gray-900">
          SSN Card
        </label>
        <div className="relative">
          <input
            type="file"
            id="ssnCard"
            className="hidden"
            onChange={(e) => handleFileInput('ssnCardFile', e)}
            accept=".pdf,.jpg,.jpeg,.png"
          />
          <label
            htmlFor="ssnCard"
            className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
          >
            <div className="flex flex-col items-center space-y-2">
              <FaCloudUploadAlt className="w-6 h-6 text-gray-400" />
              <span className="text-xs text-gray-600">Select File</span>
            </div>
          </label>
        </div>
        {documents.ssnCardFile && (
          <p className="text-xs text-gray-600 mt-1">{documents.ssnCardFile.name}</p>
        )}
      </div>

      {/* Work Authorization Document */}
      <div className="space-y-2">
        <label className="block text-xs font-semibold text-gray-900">
          Work Authorization Document
        </label>
        <div className="relative">
          <input
            type="file"
            id="workAuthorization"
            className="hidden"
            onChange={(e) => handleFileInput('workAuthorizationFile', e)}
            accept=".pdf,.jpg,.jpeg,.png"
          />
          <label
            htmlFor="workAuthorization"
            className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
          >
            <div className="flex flex-col items-center space-y-2">
              <FaCloudUploadAlt className="w-6 h-6 text-gray-400" />
              <span className="text-xs text-gray-600">Select File</span>
            </div>
          </label>
        </div>
        {documents.workAuthorizationFile && (
          <p className="text-xs text-gray-600 mt-1">{documents.workAuthorizationFile.name}</p>
        )}
      </div>

      {/* Proof of Address */}
      <div className="space-y-2">
        <label className="block text-xs font-semibold text-gray-900">
          Proof of Address
        </label>
        <div className="relative">
          <input
            type="file"
            id="proofOfAddress"
            className="hidden"
            onChange={(e) => handleFileInput('proofOfAddressFile', e)}
            accept=".pdf,.jpg,.jpeg,.png"
          />
          <label
            htmlFor="proofOfAddress"
            className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
          >
            <div className="flex flex-col items-center space-y-2">
              <FaCloudUploadAlt className="w-6 h-6 text-gray-400" />
              <span className="text-xs text-gray-600">Select File</span>
            </div>
          </label>
        </div>
        {documents.proofOfAddressFile && (
          <p className="text-xs text-gray-600 mt-1">{documents.proofOfAddressFile.name}</p>
        )}
      </div>

      {/* Additional Documents Button */}
      <div className="pt-4">
        <button
          type="button"
          disabled
          className="flex items-center space-x-2 text-gray-400 font-medium text-sm cursor-not-allowed opacity-50"
        >
          <div className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center">
            <FaPlus className="w-3 h-3 text-white" />
          </div>
          <span>Additional Documents</span>
        </button>
      </div>

      {renderSaveNextButtons()}
    </div>
  );

  const handlePreview = () => {
    // TODO: Implement preview functionality
    console.log('Preview application');
  };

  const handleSubmit = () => {
    if (!acknowledgement.agreed) {
      alert('Please acknowledge the declaration by checking the checkbox.');
      return;
    }
    // TODO: Implement submit functionality
    console.log('Submit application', { personalInfo, workExperiences, documents });
  };

  const renderAcknowledgement = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        {/* Checkbox */}
        <div className="flex items-start">
          <input
            type="checkbox"
            id="acknowledgement"
            checked={acknowledgement.agreed}
            onChange={(e) => setAcknowledgement({ agreed: e.target.checked })}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          
          {/* Declaration Text */}
          <label htmlFor="acknowledgement" className="ml-3 text-sm text-gray-900 leading-relaxed cursor-pointer">
            I hereby declare that the information provided above is true and complete to the best of my knowledge. I understand that any false information or misrepresentation may lead to disqualification or termination of employment. I also authorize the company to verify the information provided and conduct necessary background checks for employment purposes.
          </label>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={handlePreview}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Preview Your Application
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-300 rounded-lg hover:bg-gray-400 transition-colors"
        >
          Submit
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Candidate Information - Candidate Dashboard</title>
      </Helmet>
      <div className="space-y-4 sm:space-y-6">
        {/* Main Content */}
        {isMobile ? (
          /* Mobile View - Vertical Button List */
          <div className="bg-white rounded-lg shadow-md border border-gray-100 p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Candidate Information
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Welcome aboard! Please provide your details in the following sections so we can proceed with your hiring process.
            </p>
            
            <div className="space-y-3">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.id);
                    // Toggle: if clicking the same section, close it; otherwise open the clicked section
                    if (mobileSectionOpen === tab.id) {
                      setMobileSectionOpen(null);
                    } else {
                      setMobileSectionOpen(tab.id);
                    }
                  }}
                  className={`w-full px-4 py-4 text-left bg-blue-600 text-white rounded-lg flex items-center justify-between transition-colors ${
                    mobileSectionOpen === tab.id
                      ? 'bg-blue-700 shadow-md'
                      : 'hover:bg-blue-700'
                  }`}
                >
                  <span className="text-sm font-medium">{tab.label}</span>
                  <FaEdit className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* Desktop View - Tabs */
          <div className="bg-white rounded-lg shadow-md border border-gray-100">
            {/* Tabs */}
            <div className="border-b border-gray-200">
              <div className="flex flex-wrap">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 sm:px-6 py-3 text-xs sm:text-sm font-medium transition-colors border-b-2 ${
                      activeTab === tab.id
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-4 sm:p-6">
              {activeTab === 'personal' && renderPersonalInformation()}
              {activeTab === 'work' && renderWorkExperience()}
              {activeTab === 'documents' && renderDocuments()}
              {activeTab === 'acknowledgement' && renderAcknowledgement()}
            </div>
          </div>
        )}

        {/* Mobile Tab Content - Shows as a full-screen overlay when edit button is clicked */}
        {isMobile && mobileSectionOpen && (
          <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
            {/* Header with back button */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center z-10">
              <button
                type="button"
                onClick={() => setMobileSectionOpen(null)}
                className="p-2 -ml-2 text-gray-600 hover:text-gray-900"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h2 className="text-lg font-semibold text-gray-900 ml-2">
                {tabs.find(t => t.id === mobileSectionOpen)?.label || 'Form'}
              </h2>
            </div>

            {/* Form Content */}
            <div className="p-4 sm:p-6">
              {mobileSectionOpen === 'personal' && renderPersonalInformation()}
              {mobileSectionOpen === 'work' && renderWorkExperience()}
              {mobileSectionOpen === 'documents' && renderDocuments()}
              {mobileSectionOpen === 'acknowledgement' && renderAcknowledgement()}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CandidateInformation;

